import React, { useState } from 'react';

import {
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { DEFAULT_ADDITIONAL_SALARY_TYPES } from 'LixaniAPI/enums';
import Button from 'components/Button/Button';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import TextInput from 'components/TextInput/TextInput';
import CalendarPlusIcon from 'mdi-material-ui/CalendarPlus';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedClockOutRecordResourceEmployerAllowances } from 'store/employerAllowances/selectors/record-employer-allowances.selectors';
import { setAllowancesCreateParams } from 'store/records/actions/records.actions';
import uuidv4 from 'uuid/v4';
import { AllowancesUnitCount } from './AllowancesUnitCount/AllowancesUnitCount';
import AllowanceDaysSelect from './Components/AllowanceDaysSelect';

import { uniqBy } from 'lodash-es';

const useStyles = makeStyles((theme) => ({
  container: {
    ...theme.component.dialogPaper,
    padding: '8px',
  },
  dialogPaper: {
    ...theme.component.dialogPaper,
  },
}));

function AllowanceDialog({ open, setDialogOpen, timeFramedAllowanceTypes }) {
  const classes = useStyles();

  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const [selectedAllowanceType, setAllowanceType] = useState('');

  const [unitCount, setUnitCount] = useState('1');

  const isSelectedAllowanceType = selectedAllowanceType ? true : false;

  const employerAllowances = useSelector(
    selectSelectedClockOutRecordResourceEmployerAllowances
  );

  const [timeFrame, setTimeFrame] = useState({
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  });

  const invalidTimeframe = timeFrame.endDate < timeFrame.startDate;

  const allowanceTypeOptions = [
    {
      salaryName: t('record.type.sick_leave'),
      salaryType: DEFAULT_ADDITIONAL_SALARY_TYPES.SICK_LEAVE.salaryType,
    },
    {
      salaryName: t('record.type.absence_vacation_paid'),
      salaryType: DEFAULT_ADDITIONAL_SALARY_TYPES.HOLIDAY_PAY.salaryType,
    },
  ];

  const allowanceTypeOptionsWithEmployerAllowances = uniqBy(
    [...allowanceTypeOptions, ...employerAllowances],
    'salaryType'
  );

  const selectedAllowance = allowanceTypeOptionsWithEmployerAllowances.find(
    (allowance) => allowance.salaryType === selectedAllowanceType
  );

  const handleAllowanceTypeChange = (value) => {
    setAllowanceType(value);
    setUnitCount('1');
    setTimeFrame({
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
    });
  };

  const isTimeframedAllowance = timeFramedAllowanceTypes.includes(
    selectedAllowanceType
  );

  const selectAllowanceComponent = () => {
    if (isTimeframedAllowance) {
      return (
        <AllowanceDaysSelect
          timeFrame={timeFrame}
          setTimeFrame={setTimeFrame}
        ></AllowanceDaysSelect>
      );
    } else {
      return selectedAllowanceType ? (
        <AllowancesUnitCount
          t={t}
          unit={selectedAllowance.unit}
          unitCount={unitCount}
          setUnitCount={setUnitCount}
        ></AllowancesUnitCount>
      ) : null;
    }
  };

  const setAbsenceRecordsParams = () => {
    const startDate = new Date(timeFrame.startDate).toISOString();
    const endDate = new Date(timeFrame.endDate).toISOString();
    const paramsId = uuidv4();

    const isLegacySickLeaveOrPaidVacation =
      selectedAllowanceType === 'ABSENCE_SICK_LEAVE' ||
      selectedAllowanceType === 'ABSENCE_VACATION_PAID';
    const absenceRecordsParams = {
      id: paramsId,
      type: isLegacySickLeaveOrPaidVacation ? selectedAllowanceType : 'ABSENCE',
      startDate,
      endDate,
      allowanceType: 'RECORD',
      subType: isLegacySickLeaveOrPaidVacation
        ? selectedAllowanceType.replace('ABSENCE_', '')
        : selectedAllowanceType,
      salaryType: isLegacySickLeaveOrPaidVacation
        ? selectedAllowanceType.replace('ABSENCE_', '')
        : selectedAllowanceType,
    };
    dispatch(setAllowancesCreateParams(absenceRecordsParams));
  };

  const setRecordAllowancesToAdd = () => {
    const absenceRecordsParams = {
      id: uuidv4(),
      salaryType: selectedAllowanceType,
      type: selectedAllowanceType,
      unitCount,
      allowanceType: 'RECORD_ALLOWANCE',
    };
    dispatch(setAllowancesCreateParams(absenceRecordsParams));
  };

  const addAllowance = (allowanceType) => {
    const isAbsenceRecordAllowance =
      timeFramedAllowanceTypes.includes(allowanceType);

    if (isAbsenceRecordAllowance) {
      setAbsenceRecordsParams();
    } else {
      setRecordAllowancesToAdd();
    }

    setDialogOpen(false);
  };

  return (
    <Dialog
      aria-labelledby="allowance-dialog"
      classes={{ paper: classes.dialogPaper }}
      fullWidth
      maxWidth="xs"
      open={open}
    >
      <DialogHeader
        icon={<CalendarPlusIcon />}
        title={t('record.allowances')}
      />
      <DialogContent>
        <Grid container className={classes.container} spacing={2}>
          <Grid item xs={12}>
            <TextInput
              displayEmpty
              fullWidth
              id="allowance-type-select"
              label={t('calendar.type')}
              MenuProps={{
                PaperProps: {
                  className: classes.menuPaper,
                },
              }}
              onChange={(e) => handleAllowanceTypeChange(e.target.value)}
              select
              shrink
              value={selectedAllowanceType}
            >
              {allowanceTypeOptionsWithEmployerAllowances.map((item) => (
                <MenuItem
                  className={classes.menuItem}
                  key={item.salaryType}
                  value={item.salaryType}
                >
                  {item.salaryName}
                </MenuItem>
              ))}
            </TextInput>
          </Grid>
          <Grid item xs={12}>
            {selectAllowanceComponent()}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={() => setDialogOpen(false)} color="primary">
          {t('common.cancel')}
        </Button>

        <Button
          onClick={() => addAllowance(selectedAllowanceType)}
          color="primary"
          disabled={
            !isSelectedAllowanceType ||
            invalidTimeframe ||
            (!isTimeframedAllowance && !unitCount)
          }
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AllowanceDialog;
